import React from 'react';
import { FormGroup, Label, Input, Row, Col, Button } from 'reactstrap';
import { Translate } from '../../../contexts/Language';
import { Modal } from '../../../components';
import countries from '../../../helpers/countries';

// Supported currencies
const SUPPORTED_CURRENCIES = [
  { code: 'BTC', name: 'Bitcoin', type: 'Crypto' },
  { code: 'ETH', name: 'Ethereum', type: 'Crypto' },
  { code: 'USDT', name: 'USDT', type: 'Crypto' },
  { code: 'USDC', name: 'USD Coin', type: 'Crypto' },
  { code: 'BNB', name: 'BNB', type: 'Crypto' },
  { code: 'USD', name: 'United States Dollar', type: 'Fiat' },
  { code: 'EUR', name: 'Euro', type: 'Fiat' },
  { code: 'GBP', name: 'British Pound', type: 'Fiat' },
];

// Supported bank networks
const BANK_NETWORKS = [
  { code: 'BANK_SEPA', name: 'Single Euro Payments Area' },
  { code: 'BANK_ACH', name: 'Automated Clearing House' },
  { code: 'BANK_FEDWIRE', name: 'Domestic Wire Transfer' },
  { code: 'BANK_SWIFT', name: 'International Wire Transfer' },
  { code: 'BANK_FPS', name: 'Fast Payment System' },
  { code: 'BANK_AUNPP', name: 'New Payments Platform-Australia' },
];

// Supported crypto networks
const CRYPTO_NETWORKS = [
  { code: 'NETWORK_BTC', name: 'Bitcoin Blockchain' },
  { code: 'NETWORK_ETH', name: 'Ethereum Blockchain' },
  { code: 'NETWORK_BSC', name: 'Binance Smart Chain' },
  { code: 'NETWORK_TRX', name: 'Tron Blockchain' },
];

export const PaymentMethodFormModal = ({
  show,
  isEditing,
  formData,
  beneficiaries,
  onChange,
  onSave,
  onClose,
}) => {
  const transferMethod = formData.transfer_method || 'BANK';

  return (
    <Modal
      Show={show}
      Title={isEditing ? Translate('Edit Payment Method') : Translate('Add Payment Method')}
      onClose={onClose}
      xlSize
    >
      <form
        onSubmit={e => { e.preventDefault(); onSave(); }}
        style={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <FormGroup>
          <Label>
            {Translate('Beneficiary')}
            {' '}
            <span className="text-danger">*</span>
          </Label>
          <Input
            type="select"
            value={formData.beneficiary_id || ''}
            onChange={e => onChange('beneficiary_id', e.target.value)}
            required
          >
            <option value="">{Translate('Select Beneficiary')}</option>
            {beneficiaries.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>
            {Translate('Title')}
            {' '}
            <span className="text-danger">*</span>
          </Label>
          <Input
            value={formData.title || ''}
            onChange={e => onChange('title', e.target.value)}
            placeholder={Translate('Payment method title')}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>
            {Translate('Transfer Method')}
            {' '}
            <span className="text-danger">*</span>
          </Label>
          <Input
            value={transferMethod}
            onChange={e => onChange('transfer_method', e.target.value)}
            type="select"
            required
          >
            <option value="BANK">BANK</option>
            <option value="CRYPTO">CRYPTO</option>
            <option value="INTERNAL">INTERNAL</option>
          </Input>
        </FormGroup>

        {/* BANK Payment Method Fields */}
        {transferMethod === 'BANK' && (
          <>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {Translate('Network')}
                    {' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={formData.network || ''}
                    onChange={e => onChange('network', e.target.value)}
                    required
                  >
                    <option value="">{Translate('Select Network')}</option>
                    {BANK_NETWORKS.map(network => (
                      <option key={network.code} value={network.code}>
                        {network.name}
                        {' '}
(
                        {network.code}
)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {Translate('Asset Type')}
                    {' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={formData.asset_type || ''}
                    onChange={e => onChange('asset_type', e.target.value)}
                    required
                  >
                    <option value="">{Translate('Select Currency')}</option>
                    {SUPPORTED_CURRENCIES.filter(c => c.type === 'Fiat').map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.name}
                        {' '}
(
                        {currency.code}
)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{Translate('Account Number')}</Label>
                  <Input
                    value={formData.account_number || ''}
                    onChange={e => onChange('account_number', e.target.value)}
                    placeholder={Translate('Bank account number')}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{Translate('IBAN')}</Label>
                  <Input
                    value={formData.iban || ''}
                    onChange={e => onChange('iban', e.target.value)}
                    placeholder={Translate('International Bank Account Number')}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>{Translate('BIC/SWIFT Code')}</Label>
                  <Input
                    value={formData.bic || ''}
                    onChange={e => onChange('bic', e.target.value)}
                    placeholder={Translate('Bank Identifier Code')}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>{Translate('Routing Number')}</Label>
                  <Input
                    value={formData.routing_number || ''}
                    onChange={e => onChange('routing_number', e.target.value)}
                    placeholder={Translate('Bank routing number')}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>{Translate('Bank Identifier')}</Label>
                  <Input
                    value={formData.bank_identifier || ''}
                    onChange={e => onChange('bank_identifier', e.target.value)}
                    placeholder={Translate('Sort code/BSB')}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>{Translate('Reference')}</Label>
              <Input
                value={formData.reference || ''}
                onChange={e => onChange('reference', e.target.value)}
                placeholder={Translate('Payment reference')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{Translate('Bank Name')}</Label>
              <Input
                value={formData.bank_name || ''}
                onChange={e => onChange('bank_name', e.target.value)}
                placeholder={Translate('Name of the bank')}
                required
              />
            </FormGroup>

            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label>{Translate('Bank Address')}</Label>
                  <Input
                    value={formData.bank_address || ''}
                    onChange={e => onChange('bank_address', e.target.value)}
                    placeholder={Translate('Bank address')}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>{Translate('Bank Country')}</Label>
                  <Input
                    value={formData.bank_country || ''}
                    onChange={e => onChange('bank_country', e.target.value)}
                    type="select"
                    required
                  >
                    {
                      countries.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))
                    }
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <hr />
            <h6 className="text-muted">{Translate('Intermediary Bank (Optional)')}</h6>

            <FormGroup>
              <Label>{Translate('Intermediary Bank Name')}</Label>
              <Input
                value={formData.intermediary_bank_name || ''}
                onChange={e => onChange('intermediary_bank_name', e.target.value)}
                placeholder={Translate('Intermediary bank name')}
                required
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{Translate('Intermediary Account Number')}</Label>
                  <Input
                    value={formData.intermediary_account_number || ''}
                    onChange={e => onChange('intermediary_account_number', e.target.value)}
                    placeholder={Translate('Intermediary account number')}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{Translate('Intermediary BIC')}</Label>
                  <Input
                    value={formData.intermediary_bic || ''}
                    onChange={e => onChange('intermediary_bic', e.target.value)}
                    placeholder={Translate('Intermediary BIC/SWIFT')}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </>
        )}

        {/* CRYPTO Payment Method Fields */}
        {transferMethod === 'CRYPTO' && (
          <>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {Translate('Network')}
                    {' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={formData.network || ''}
                    onChange={e => onChange('network', e.target.value)}
                    required
                  >
                    <option value="">{Translate('Select Network')}</option>
                    {CRYPTO_NETWORKS.map(network => (
                      <option key={network.code} value={network.code}>
                        {network.name}
                        {' '}
(
                        {network.code}
)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {Translate('Asset Type')}
                    {' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={formData.asset_type || ''}
                    onChange={e => onChange('asset_type', e.target.value)}
                    required
                  >
                    <option value="">{Translate('Select Cryptocurrency')}</option>
                    {SUPPORTED_CURRENCIES.filter(c => c.type === 'Crypto').map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.name}
                        {' '}
(
                        {currency.code}
)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>
                {Translate('Wallet Address')}
                {' '}
                <span className="text-danger">*</span>
              </Label>
              <Input
                value={formData.address || ''}
                onChange={e => onChange('address', e.target.value)}
                placeholder={Translate('Cryptocurrency wallet address')}
                required
              />
            </FormGroup>
          </>
        )}

        {/* INTERNAL Payment Method Fields */}
        {transferMethod === 'INTERNAL' && (
          <>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {Translate('Asset Type')}
                    {' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={formData.asset_type || ''}
                    onChange={e => onChange('asset_type', e.target.value)}
                    required
                  >
                    <option value="">{Translate('Select Currency')}</option>
                    {SUPPORTED_CURRENCIES.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.name}
                        {' '}
(
                        {currency.code}
)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {Translate('UID (Customer ID)')}
                    {' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    value={formData.uid || ''}
                    onChange={e => onChange('uid', e.target.value)}
                    placeholder={Translate('Numerical identifier of the customer')}
                    type="number"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </>
        )}

        <div className="text-right mt-3">
          <Button color="secondary" onClick={onClose} className="mr-2">
            {Translate('Cancel')}
          </Button>
          <Button color="primary" type="submit">
            {Translate('Save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
